import { Link, useOutletContext } from "react-router-dom";
import { formatNANP } from "../../utils/phone";
import { buildMapEmbedUrl } from "../../utils/portalContact";
import { resolveAssetUrl } from "../../utils/assetUrl";

const buildHeroStyle = (backgroundUrl) => {
  if (!backgroundUrl) {
    return {
      backgroundImage:
        "linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(15, 23, 42, 0.7))",
    };
  }
  const safeUrl = encodeURI(backgroundUrl).replace(/"/g, "%22");
  return {
    backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.45)), url("${safeUrl}")`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const parseTimeLabel = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return "";
  const parts = timeStr.split(":");
  if (parts.length < 2) return "";
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return "";
  const meridiem = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${meridiem}`;
};

const normalizeHoursMap = (hours) => {
  if (!hours) return {};
  if (Array.isArray(hours)) {
    const map = {};
    hours.forEach((row) => {
      const idx = Number(row?.day_of_week);
      const key = DAY_KEYS[idx];
      if (!key) return;
      map[key] = {
        closed: row?.is_closed ?? row?.closed ?? false,
        open: row?.open_time ?? row?.open ?? "",
        close: row?.close_time ?? row?.close ?? "",
      };
    });
    return map;
  }
  if (typeof hours === "object") {
    return hours;
  }
  return {};
};

export default function PortalLanding() {
  const { settings, salonId } = useOutletContext();

  const heroTitle =
    String(settings?.hero_title || "").trim() ||
    "Book your next visit in seconds.";
  const heroSubtitle =
    String(settings?.hero_subtitle || "").trim() ||
    "Reserve appointments, manage your profile, and stay connected with your salon.";
  const backgroundStyle = buildHeroStyle(
    resolveAssetUrl(settings?.background_url || "")
  );
  const googleReviewUrl = String(settings?.google_review_url || "").trim();
  const hasGoogleReviewUrl = Boolean(googleReviewUrl);
  const addressText = String(settings?.address_text || "").trim();
  const phone = String(settings?.phone || "").trim();
  const hoursMap = normalizeHoursMap(settings?.hours);
  const mapEmbedUrl = settings?.map_embed_url || buildMapEmbedUrl(addressText);
  const hasSalonInfo = Boolean(addressText || phone || Object.keys(hoursMap).length);
  const formattedHours = DAY_KEYS.map((key, idx) => {
    const entry = hoursMap?.[key] || {};
    const isClosed = entry.closed === true || entry.is_closed === true;
    if (isClosed) {
      return { label: DAY_LABELS[idx], value: "Closed" };
    }
    const openLabel = parseTimeLabel(entry.open || entry.open_time || "");
    const closeLabel = parseTimeLabel(entry.close || entry.close_time || "");
    if (!openLabel || !closeLabel) {
      return { label: DAY_LABELS[idx], value: "By appointment" };
    }
    return { label: DAY_LABELS[idx], value: `${openLabel} - ${closeLabel}` };
  });


  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-12 md:py-16 text-white" style={backgroundStyle}>
          <div className="max-w-2xl space-y-4">
            <h2 className="text-3xl md:text-4xl font-semibold leading-tight">
              {heroTitle}
            </h2>
            <p className="text-base md:text-lg text-white/90">
              {heroSubtitle}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to={`/portal/salon/${salonId}/book`}
                className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800"
              >
                Start Booking
              </Link>
              <Link
                to={`/portal/salon/${salonId}/login`}
                className="rounded-full bg-rose-500 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-rose-600"
              >
                Login
              </Link>
              <Link
                to={`/portal/salon/${salonId}/register`}
                className="rounded-full border border-white/40 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-white/10"
              >
                Register
              </Link>
            </div>
          </div>
        </div>
      </div>

      {hasGoogleReviewUrl && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Leave a Review
            </h3>
            <p className="text-sm text-slate-600">
              Your feedback helps us improve and serve you better.
            </p>
          </div>
          <a
            href={googleReviewUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-rose-500 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-rose-600"
          >
            Leave a Review
          </a>
        </div>
      )}

      {hasSalonInfo && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Salon Info</h3>
            <p className="text-sm text-slate-500">
              Address, hours, and contact details.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.1fr_1fr]">
            <div className="space-y-4">
              {addressText && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Address
                  </div>
                  <div className="mt-2 whitespace-pre-line text-sm text-slate-700">
                    {addressText}
                  </div>
                </div>
              )}
              {phone && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Phone
                  </div>
                  <div className="mt-2 text-sm text-slate-700">
                    <a
                      href={`tel:${phone}`}
                      className="font-semibold text-rose-600 hover:text-rose-500"
                    >
                      {formatNANP(phone)}
                    </a>
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Hours
                </div>
                <div className="mt-2 space-y-1 text-sm text-slate-700">
                  {formattedHours.map((row) => (
                    <div
                      key={row.label}
                      className="grid grid-cols-[64px_auto] gap-x-4"
                    >
                      <span className="font-semibold text-slate-600">
                        {row.label}
                      </span>
                      <span>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {mapEmbedUrl && (
              <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white h-[260px] md:h-[320px] relative">
                <iframe
                  title="Salon location map"
                  src={mapEmbedUrl}
                  className="absolute inset-0 w-full h-full block"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-slate-900">Easy booking</h3>
          <p className="mt-2 text-sm text-slate-600">
            Pick services and times that fit your schedule and confirm in minutes.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-slate-900">Manage your profile</h3>
          <p className="mt-2 text-sm text-slate-600">
            Update contact details and keep your salon informed.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-slate-900">Stay connected</h3>
          <p className="mt-2 text-sm text-slate-600">
            Access your portal anytime and keep track of upcoming visits.
          </p>
        </div>
      </div>
    </div>
  );
}
